import { Phone, Mail, MapPin, ArrowUpRight, Instagram } from "lucide-react";
import { Reveal, MaskedLine } from "./Reveal";
import { scrollToId } from "../hooks/useLenis";

export const ContactAndFooter = () => (
  <footer id="kontakt" data-testid="footer" className="relative bg-[#090d16] grain overflow-hidden">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-24 sm:pt-32 pb-10">
      <div className="grid lg:grid-cols-2 gap-14 mb-20">
        <div className="text-center lg:text-left">
          <Reveal>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-green-400 mb-5">Kontakt</p>
          </Reveal>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-[clamp(2.2rem,9vw,4.5rem)] text-white">
            <MaskedLine>Få ett pris</MaskedLine>
            <MaskedLine delay={0.1}>anpassat till</MaskedLine>
            <MaskedLine delay={0.2}><span className="text-[#16a34a]">just din flytt.</span></MaskedLine>
          </h2>
          <Reveal delay={0.3}>
            <button
              data-testid="footer-cta-button"
              onClick={() => scrollToId("#offert")}
              className="group mt-9 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 transition-colors duration-300"
            >
              Få offert nu
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>

        <div className="flex flex-col justify-end gap-2">
          <Reveal delay={0.15}>
            <a
              data-testid="footer-phone-link"
              href="tel:+46707272941"
              className="group flex items-center justify-between border-b border-white/10 py-5 text-slate-300 hover:text-white transition-colors"
            >
              <span className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-green-400" />
                <span className="font-display font-bold text-xl sm:text-2xl">070-727 29 41</span>
              </span>
              <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-[#16a34a] transition-colors" />
            </a>
            <a
              data-testid="footer-email-link"
              href="mailto:kontakt@studentflyttarna.se"
              className="group flex items-center justify-between border-b border-white/10 py-5 text-slate-300 hover:text-white transition-colors"
            >
              <span className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-green-400" />
                <span className="font-display font-bold text-lg sm:text-2xl break-all">kontakt@studentflyttarna.se</span>
              </span>
              <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-[#16a34a] transition-colors shrink-0" />
            </a>
            <a
              data-testid="footer-instagram-link"
              href="https://instagram.com/studentflyttarna"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between border-b border-white/10 py-5 text-slate-300 hover:text-white transition-colors"
            >
              <span className="flex items-center gap-4">
                <Instagram className="w-5 h-5 text-green-400" />
                <span className="font-display font-bold text-xl sm:text-2xl">@studentflyttarna</span>
              </span>
              <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-[#16a34a] transition-colors" />
            </a>
            <a
              data-testid="footer-tiktok-link"
              href="https://tiktok.com/@studentflyttarna"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between border-b border-white/10 py-5 text-slate-300 hover:text-white transition-colors"
            >
              <span className="flex items-center gap-4">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-green-400" aria-hidden>
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 1 0 15.7 15.7V8.75a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
                <span className="font-display font-bold text-xl sm:text-2xl">TikTok</span>
              </span>
              <ArrowUpRight className="w-5 h-5 text-slate-500 group-hover:text-[#16a34a] transition-colors" />
            </a>
            <div data-testid="footer-location" className="flex items-center gap-4 py-5 text-slate-400">
              <MapPin className="w-5 h-5 text-green-400" />
              <span className="font-mono2 text-xs uppercase tracking-[0.2em]">Baserade i Umeå · Hela Norrland</span>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal>
        <p data-testid="footer-signoff" className="font-display font-extrabold text-white/[0.06] leading-none tracking-tight text-[16.5vw] lg:text-[13rem] select-none whitespace-nowrap">
          Vi flyttar Norrland
        </p>
      </Reveal>

      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="footer-copyright">
          © {new Date().getFullYear()} Studentflyttarna
        </span>
        <div className="flex flex-wrap gap-5">
          <a
            data-testid="footer-privacy-link"
            href="/integritetspolicy"
            className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-[#16a34a] transition-colors"
          >
            Integritetspolicy
          </a>
          <a
            data-testid="footer-terms-link"
            href="/anvandarvillkor"
            className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-[#16a34a] transition-colors"
          >
            Användarvillkor
          </a>
          <a
            data-testid="footer-cookies-link"
            href="/cookiepolicy"
            className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-[#16a34a] transition-colors"
          >
            Cookiepolicy
          </a>
        </div>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Umeå → Östersund · Sundsvall · Luleå
        </span>
      </div>
    </div>
  </footer>
);
